import {
    Flex,
    HStack,
    Image,
    IconButton,
    useDisclosure,
    VStack,
    useBreakpointValue,
    Slide,
    Box,
} from "@chakra-ui/react";
import { useLocation } from "react-router-dom";
import { Suspense, useEffect } from 'react';
import '../../styles/Header.css';
import { HamburgerIcon } from "@chakra-ui/icons";
import { Link, CustomWalletMultiButton } from "../../components";
import { LAST_STAKE_ASSET_KEY } from "../../config/assets";
import { HeaderComingSoonLink } from "./HeaderComingSoonLink";
import { HeaderMoreMenu } from "./HeaderMoreMenu";
import { ScratchWord } from "../../features/drip/ScratchWord";
import { partnerForPathname, PINNED_NAV_PARTNER } from "../../constants/partners";
import { ThemeColor } from "../../theme/palette";

const getStakePath = () => {
    const lastAsset = localStorage.getItem(LAST_STAKE_ASSET_KEY) ?? "sol";
    return `/stake/${lastAsset}`;
};

const isStakeRoute = (pathname: string) =>
    pathname.startsWith("/stake") ||
    pathname.startsWith("/restake") ||
    pathname.startsWith("/redeem");

export const Header = () => {
    const logoSrc = useBreakpointValue({
        base: "/logo-mobile.png",
        md: "/logo.png",
    });

    const logoHeight = useBreakpointValue({
        base: '16px',
        md: '20px',
    });

    const location = useLocation();
    const { isOpen, onClose, onToggle } = useDisclosure();

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
    }, [isOpen]);

    const stakePath = getStakePath();
    // Swap the "Drip" nav item for the partner name in its accent colour,
    // followed by a scratched-out "Drip" that still links to /drip.
    //
    // A pinned partner brands the item on every route; a route partner (e.g.
    // /ansem, /zcash) takes over on its own page. With no pinned partner and
    // off a partner page, the item is a plain "Drip".
    const routePartner = partnerForPathname(location.pathname);
    const navPartner = routePartner ?? PINNED_NAV_PARTNER;
    const navPartnerLabel = navPartner
        ? navPartner.navLabel ?? navPartner.tokenSymbol
        : null;
    // The partner item now renders off its own page too, so "active" has to be
    // derived rather than assumed.
    const isNavPartnerActive = !!navPartner && location.pathname === `/${navPartner.slug}`;
    const isDripActive = location.pathname === "/drip";

    return (
        <>
            <Flex
                as="header"
                className="header-nav"
                h="8.8rem"
                w="100%"
                px={{ base: "6rem", sm: "8rem", md: "12rem", lg: "20rem", xl: "30rem" }}
                alignItems="center"
                justifyContent="space-between"
                zIndex="11"
                onClick={isOpen ? onClose : undefined}
            >
                <HStack spacing={{ base: 4, sm: 4, md: 4, lg: 6 }}>
                    <IconButton
                        aria-label="Open menu"
                        icon={<HamburgerIcon color="white" w={6} h={6} />}
                        display={{ base: 'block', lg: 'none' }}
                        onClick={onToggle}
                        variant="unstyled"
                    />

                    <Link to={stakePath}>
                        <Image src={logoSrc} alt="Kyros" height={logoHeight} />
                    </Link>

                    <HStack spacing={{ base: 4, sm: 4, md: 4, lg: 6 }} display={{ base: 'none', lg: 'flex' }} ml={4}>
                        <Link
                            to={stakePath}
                            className={isStakeRoute(location.pathname) ? "active nav-link" : "nav-link"}
                        >
                            Stake
                        </Link>
                        {navPartner ? (
                            <HStack spacing={2}>
                                <Link
                                    to={`/${navPartner.slug}`}
                                    className={isNavPartnerActive ? "active nav-link" : "nav-link"}
                                    color={navPartner.accent}
                                    _hover={{ color: navPartner.accent }}
                                    sx={{ "&::after": { background: `${navPartner.accent} !important` } }}
                                >
                                    Drip <b>{navPartnerLabel}</b>
                                </Link>
                                <Link
                                    to="/drip"
                                    className={isDripActive ? "active nav-link" : "nav-link"}
                                    aria-label="Drip"
                                    color={ThemeColor.KAIROS}
                                    _hover={{ color: ThemeColor.KAIROS }}
                                >
                                    <ScratchWord strokeUrl={navPartner.scratchUrl}>Drip</ScratchWord>
                                </Link>
                            </HStack>
                        ) : (
                            <Link
                                to="/drip"
                                className={isDripActive ? "active nav-link" : "nav-link"}
                            >
                                Drip
                            </Link>
                        )}
                        <HeaderComingSoonLink label="Loot" />
                        <HeaderMoreMenu />
                    </HStack>
                </HStack>

                <HStack spacing={4}>
                    <Suspense>
                        <CustomWalletMultiButton />
                    </Suspense>
                </HStack>
            </Flex>

            {isOpen && (
                <Box
                    position="fixed"
                    top="8.8rem"
                    left="0"
                    width="100vw"
                    height="calc(100vh - 8.8rem)"
                    bg="rgba(0, 0, 0, 0.5)"
                    zIndex="9"
                    onClick={onClose}
                />
            )}

            <Slide direction="left" in={isOpen} style={{ zIndex: 10, position: 'fixed', top: '8.8rem' }}>
                <Box
                    bg="rgba(0, 0, 0, 0.5)"
                    backdropFilter="blur(10px)"
                    w="full"
                    h="calc(100vh - 8.8rem)"
                    position="fixed"
                    overflowY="auto"
                >
                    <VStack align="start" spacing={4} mt={4} pl={4}>
                        <Link
                            to={stakePath}
                            width="90%"
                            onClick={onClose}
                            className={`menu-button ${isStakeRoute(location.pathname) ? "active" : ""}`}
                        >
                            Stake
                        </Link>
                        {navPartner ? (
                            <>
                                <Link
                                    to={`/${navPartner.slug}`}
                                    width="90%"
                                    onClick={onClose}
                                    className={`menu-button ${isNavPartnerActive ? "active" : ""}`}
                                    color={navPartner.accent}
                                    _hover={{ color: navPartner.accent }}
                                >
                                    {navPartnerLabel}
                                </Link>
                                <Link
                                    to="/drip"
                                    width="90%"
                                    onClick={onClose}
                                    className={`menu-button ${isDripActive ? "active" : ""}`}
                                    aria-label="Drip"
                                    color={ThemeColor.KAIROS}
                                >
                                    <ScratchWord strokeUrl={navPartner.scratchUrl}>Drip</ScratchWord>
                                </Link>
                            </>
                        ) : (
                            <Link
                                to="/drip"
                                width="90%"
                                onClick={onClose}
                                className={`menu-button ${isDripActive ? "active" : ""}`}
                            >
                                Drip
                            </Link>
                        )}
                        <HeaderComingSoonLink
                            label="Loot"
                            className="menu-button menu-button--disabled"
                        />
                        <Link
                            to="/kyros"
                            width="90%"
                            onClick={onClose}
                            className={`menu-button ${location.pathname === "/kyros" ? "active" : ""}`}
                        >
                            $KYROS
                        </Link>
                    </VStack>
                </Box>
            </Slide>
        </>
    );
};
